import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Input } from '@/components/ui/Input';
import {
  kbQueryKeys,
  listKnowledgeBaseDocuments,
  type KnowledgeBaseDocumentListParams,
} from '@/services/knowledge-base.service';

export function KnowledgeLibraryPage() {
  const [q, setQ] = useState('');
  const params: KnowledgeBaseDocumentListParams = useMemo(
    () => ({ q: q.trim() || undefined, perPage: 50, status: 'active' }),
    [q],
  );

  const docsQuery = useQuery({
    queryKey: kbQueryKeys.documents(params),
    queryFn: () => listKnowledgeBaseDocuments(params),
  });

  return (
    <div className="p-6">
      <PageHeader title="Knowledge Base" subtitle="Search SOPs, training, and policy articles." />
      <div className="relative mb-3">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search articles..."
          className="pl-9"
        />
      </div>
      <div className="rounded-lg border border-slate-200 bg-white">
        {docsQuery.isLoading ? (
          <div className="p-6 text-sm text-slate-500">Searching...</div>
        ) : !docsQuery.data?.data.length ? (
          <div className="p-6 text-sm text-slate-500">No articles found.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {docsQuery.data.data.map((doc) => (
              <li key={doc.kbDocumentId}>
                <Link
                  to={`/knowledge/article/${doc.kbDocumentId}`}
                  className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-slate-900">{doc.title}</div>
                    <div className="text-xs text-slate-500">
                      {doc.category || '—'}
                      {doc.tags.length > 0 ? ` • ${doc.tags.join(', ')}` : ''}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
